import React, { useState, useEffect, useCallback } from 'react';
import { PiLightbulbLight, PiXLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';

const STORAGE_PREFIX = 'pons_wizard_tip_';

interface WizardStepTipProps {
  /** Unique key for this tip (e.g. 'matter-wizard-basics') */
  tipId: string;
  /** Tip headline */
  title: string;
  /** Tip body text */
  content: string;
}

/**
 * A dismissible contextual tip shown at the top of a wizard step.
 * Remembers dismissal per-step via localStorage.
 * Collapses smoothly with a transition.
 */
const WizardStepTip: React.FC<WizardStepTipProps> = ({ tipId, title, content }) => {
  const { t } = useTranslation();
  const storageKey = `${STORAGE_PREFIX}${tipId}`;

  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'dismissed';
    } catch {
      return false;
    }
  });

  const [isVisible, setIsVisible] = useState(false);

  // Animate in after mount
  useEffect(() => {
    if (isDismissed) return;
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, [isDismissed]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    // Wait for transition to complete before removing from DOM
    setTimeout(() => {
      setIsDismissed(true);
      try {
        localStorage.setItem(storageKey, 'dismissed');
      } catch {
        // localStorage may be unavailable
      }
    }, 200);
  }, [storageKey]);

  if (isDismissed) return null;

  return (
    <div
      className={`overflow-hidden transition-all duration-200 ease-out ${
        isVisible ? 'max-h-40 opacity-100 mb-4' : 'max-h-0 opacity-0 mb-0'
      }`}
    >
      <div className="flex items-start gap-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 px-4 py-3">
        <div className="flex-shrink-0 mt-0.5">
          <PiLightbulbLight className="w-4 h-4 text-primary-500 dark:text-primary-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">
            {t(title)}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-relaxed">
            {t(content)}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-800/30 transition-colors duration-150"
          aria-label={t('Dismiss tip')}
        >
          <PiXLight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default WizardStepTip;
